import React, { useState, useEffect } from 'react';
import { Plus, Search, TrendingUp, Pencil, Trash2, RotateCcw, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateOpportunityModal from '../../../components/admin/crm/CreateOpportunityModal';
import EditOpportunityModal from '../../../components/admin/crm/EditOpportunityModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  prospecting: 'bg-gray-100 text-gray-800',
  qualification: 'bg-blue-100 text-blue-800',
  proposal: 'bg-yellow-100 text-yellow-800',
  negotiation: 'bg-orange-100 text-orange-800',
  closed_won: 'bg-green-500 text-white',
  closed_lost: 'bg-red-100 text-red-800'
};

const STAGE_LABELS = {
  prospecting: 'Prospecting',
  qualification: 'Qualification',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost'
};

const Opportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total_count: 0, total_value: 0, avg_value: 0, win_rate: 0 });
  const [clientsMap, setClientsMap] = useState({});   // id → company_name (for display)
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchOpportunities();
    fetchCount();
    fetchStats();
    fetchClientsMap();
  }, [pagination, searchTerm, stageFilter]);

  const fetchClientsMap = async () => {
    try {
      const r = await fetch(`${API_URL}/api/crm/clients?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const list = await r.json();
      const map = {};
      if (Array.isArray(list)) {
        list.forEach((c) => { map[c.id] = c.company_name || '—'; });
      }
      setClientsMap(map);
    } catch { /* silent */ }
  };

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const stageParam = stageFilter ? `&stage=${stageFilter}` : '';
      const response = await fetch(
        `${API_URL}/api/crm/opportunities?skip=${pagination.skip}&limit=${pagination.limit}${searchParam}${stageParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch opportunities');

      const data = await response.json();
      setOpportunities(data);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load opportunities',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const searchParam = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const stageParam = stageFilter ? (searchParam ? '&' : '?') + `stage=${stageFilter}` : '';
      const response = await fetch(
        `${API_URL}/api/crm/opportunities/count${searchParam}${stageParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch count');

      const data = await response.json();
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/crm/opportunities/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDelete = async (opportunityId) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/opportunities/${opportunityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete opportunity');

      toast({
        title: 'Success',
        description: 'Opportunity deleted successfully'
      });
      fetchOpportunities();
      fetchCount();
      fetchStats();
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete opportunity',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (opportunityId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/opportunities/${opportunityId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore opportunity');

      toast({
        title: 'Success',
        description: 'Opportunity restored successfully'
      });
      fetchOpportunities();
      fetchStats();
    } catch (error) {
      console.error('Error restoring opportunity:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore opportunity',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsEditModalOpen(true);
  };

  const formatCurrency = (value, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < totalCount) {
      setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <TrendingUp className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Opportunities</h2>
              <p className="text-sm text-[#6C757D]">Manage your sales pipeline</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Opportunity
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6C757D]" />
              <input
                type="text"
                placeholder="Search opportunities by name or description..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>

            {/* Stage Filter */}
            <select
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Stages</option>
              <option value="prospecting">Prospecting</option>
              <option value="qualification">Qualification</option>
              <option value="proposal">Proposal</option>
              <option value="negotiation">Negotiation</option>
              <option value="closed_won">Closed Won</option>
              <option value="closed_lost">Closed Lost</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Opportunities</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.total_count}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Pipeline Value</p>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {formatCurrency(stats.total_value)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Avg Value</p>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {formatCurrency(stats.avg_value)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Win Rate</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.win_rate}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Opportunities List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading opportunities...</p>
          </CardContent>
        </Card>
      ) : opportunities.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm || stageFilter ? 'No opportunities found matching your filters.' : 'No opportunities yet. Add your first opportunity!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {opportunities.map((opp) => (
            <Card key={opp.id} className={opp.is_deleted ? 'opacity-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#2B2D42]">
                        {opp.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${STAGE_COLORS[opp.stage]}`}>
                        {STAGE_LABELS[opp.stage]}
                      </span>
                      {opp.is_deleted && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Deleted
                        </span>
                      )}
                      <div className="flex items-center space-x-1">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-semibold text-green-600">
                          {formatCurrency(opp.value, opp.currency)}
                        </span>
                      </div>
                      {opp.tags && opp.tags.length > 0 && (
                        <div className="flex gap-1">
                          {opp.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-[#6C757D]">
                      <div>
                        <span className="font-medium">Client:</span>{' '}
                        <span data-testid={`opp-client-name-${opp.id}`}>
                          {clientsMap[opp.client_id] ||
                           (opp.client_id ? (
                             <span className="text-amber-700" title="Client record not found">
                               Unlinked client (id: {String(opp.client_id).slice(0,8)}…)
                             </span>
                           ) : '— no client')}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Probability:</span> {opp.probability}%
                      </div>
                      {opp.expected_close_date && (
                        <div>
                          <span className="font-medium">Expected Close:</span>{' '}
                          {new Date(opp.expected_close_date).toLocaleDateString()}
                        </div>
                      )}
                      {opp.assigned_to && (
                        <div>
                          <span className="font-medium">Assigned To:</span> {opp.assigned_to}
                        </div>
                      )}
                    </div>
                    {opp.description && (
                      <div className="mt-3 text-sm text-[#6C757D]">
                        <span className="font-medium">Description:</span> {opp.description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {opp.is_deleted ? (
                      <Button
                        onClick={() => handleRestore(opp.id)}
                        variant="outline"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => handleEdit(opp)}
                          variant="outline"
                          size="sm"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(opp.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <Button
            onClick={handlePrevPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={pagination.skip + pagination.limit >= totalCount}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <CreateOpportunityModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchOpportunities();
          fetchCount();
          fetchStats();
        }}
      />

      {selectedOpportunity && (
        <EditOpportunityModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedOpportunity(null);
          }}
          opportunity={selectedOpportunity}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedOpportunity(null);
            fetchOpportunities();
            fetchStats();
          }}
        />
      )}
    </div>
  );
};

export default Opportunities;
